/**
 * 計助ダッシュボード用グラフユーティリティ
 * - gridData からグループ別平均を算出して棒グラフ用データに変換
 * - P 値に基づく有意差マーク（***, **, *）の配置情報を算出（重ならないよう Y オフセットを段階化）
 * - レガシー棒グラフ用: 平均・SD 付き BarData の生成
 */

import { calculateBasicStatistics } from '../../../../utils/statistics/basicStats';
import {
  calculateNiceDomain,
  getColor,
  keisukeCategoryLabelFromCell,
  keisukeNumericCellValue,
} from '../charts/chartUtils';
import type { BarData } from '../charts/types';
import type {
  DataRow,
  VariableMapping,
} from '../../../../stores/systems/keisuke/dashboardStore';
import {
  isKeisukeGenericGroupColumnKey,
  isKeisukeGenericValueColumnKey,
  isKeisukeWeightColumnKey,
} from '../../../../utils/keisukeStockColumnLabels';

/** グループ列名と値列名を推定（先頭2列または 'group'/'value'／群／値） */
export function getGroupAndValueColumns(headers: string[]): { groupCol: string; valueCol: string } {
  if (headers.length >= 2) {
    const groupCol = headers.find((h) => isKeisukeGenericGroupColumnKey(h));
    const valueCol = headers.find(
      (h) => isKeisukeGenericValueColumnKey(h) || isKeisukeWeightColumnKey(h)
    );
    if (groupCol && valueCol) return { groupCol, valueCol };
    return { groupCol: headers[0]!, valueCol: headers[1]! };
  }
  return { groupCol: 'group', valueCol: 'value' };
}

/** 対応あり t 検定のワイド表を棒グラフ用の長形式へ（列名を群ラベルとして用いる） */
const DASHBOARD_PAIR_GROUP_KEY = 'group';
const DASHBOARD_PAIR_VALUE_KEY = 'value';

/** 対応なしワイド2列で、群名同士を group/value に誤設定しているか */
function isWideTwoGroupHeadersMisMapped(
  gridHeaders: string[],
  mapping: VariableMapping
): boolean {
  const gc = mapping.groupColumn;
  const vc = mapping.valueColumn;
  return (
    gridHeaders.length === 2 &&
    Boolean(gc && vc) &&
    gc === gridHeaders[0] &&
    vc === gridHeaders[1]
  );
}

function expandWideTwoGroupRows(
  gridData: DataRow[],
  headerA: string,
  headerB: string,
  valueKey: string
): DataRow[] {
  const rows: DataRow[] = [];
  for (const row of gridData) {
    const va = keisukeNumericCellValue(row[headerA]);
    const vb = keisukeNumericCellValue(row[headerB]);
    if (va !== null) {
      rows.push({
        [DASHBOARD_PAIR_GROUP_KEY]: headerA,
        [valueKey]: va,
      } as DataRow);
    }
    if (vb !== null) {
      rows.push({
        [DASHBOARD_PAIR_GROUP_KEY]: headerB,
        [valueKey]: vb,
      } as DataRow);
    }
  }
  return rows;
}

function resolveWideValueKey(
  gridHeaders: string[],
  mapping: VariableMapping,
  wideMisMapped: boolean
): string {
  const vc = mapping.valueColumn ?? '';
  if (mapping.valueAxisLabel && !gridHeaders.includes(mapping.valueAxisLabel)) {
    return mapping.valueAxisLabel;
  }
  if (!wideMisMapped && vc && !gridHeaders.includes(vc)) {
    return vc;
  }
  return DASHBOARD_PAIR_VALUE_KEY;
}

export type DashboardBarChartGridOptions = {
  type?: string;
  testType?: 'unpaired' | 'paired';
  anovaType?: 'oneWay' | 'twoWay' | 'repeatedMeasures';
  mapping?: VariableMapping;
};

/**
 * ダッシュボードの t 検定・ANOVA 棒グラフが読む gridData と列名を決定する。
 * 対応あり t 検定では 2 列とも数値のため、従来の「先頭列＝群」推定では barData が空になりがちなので長形式に展開する。
 */
export function resolveDashboardBarChartGrid(
  gridData: DataRow[],
  gridHeaders: string[],
  opts: DashboardBarChartGridOptions
): { gridData: DataRow[]; groupCol: string; valueCol: string } {
  const type = opts.type ?? 't-test';
  const mapping = opts.mapping ?? {};
  const anovaType = opts.anovaType;
  const testType = opts.testType ?? 'unpaired';

  if (
    type === 't-test' &&
    testType === 'unpaired' &&
    mapping.groupColumn &&
    mapping.valueColumn &&
    gridHeaders.includes(mapping.groupColumn) &&
    gridHeaders.includes(mapping.valueColumn)
  ) {
    if (!isWideTwoGroupHeadersMisMapped(gridHeaders, mapping)) {
      return { gridData, groupCol: mapping.groupColumn, valueCol: mapping.valueColumn };
    }
  }

  /**
   * 対応なし・2 列ワイド（例: A 列と B 列＝2 群）だが mapping が表の列名と一致しない場合、
   * 長形式（group, value）に直してから棒グラフ用集計する。
   * 既に group+value の長形式（先頭2列）の場合は下の getGroupAndValueColumns に任せ、二重展開しない。
   */
  if (type === 't-test' && testType === 'unpaired' && gridHeaders.length === 2) {
    const h0 = gridHeaders[0]!;
    const h1 = gridHeaders[1]!;
    const looksLikeLongFormatPair =
      (isKeisukeGenericGroupColumnKey(h0) &&
        (isKeisukeGenericValueColumnKey(h1) || isKeisukeWeightColumnKey(h1))) ||
      (h0.toLowerCase() === 'condition' &&
        (isKeisukeGenericValueColumnKey(h1) || h1.toLowerCase() === 'value'));
    if (!looksLikeLongFormatPair) {
      const gc = mapping.groupColumn ?? '';
      const vc = mapping.valueColumn ?? '';
      const mappingMatches =
        Boolean(gc && vc && gridHeaders.includes(gc) && gridHeaders.includes(vc));
      const wideMisMapped = isWideTwoGroupHeadersMisMapped(gridHeaders, mapping);
      if (!mappingMatches || wideMisMapped) {
        const a = gridHeaders[0]!;
        const b = gridHeaders[1]!;
        const valueKey = resolveWideValueKey(gridHeaders, mapping, wideMisMapped);
        const rows = expandWideTwoGroupRows(gridData, a, b, valueKey);
        if (rows.length > 0) {
          return {
            gridData: rows,
            groupCol: DASHBOARD_PAIR_GROUP_KEY,
            valueCol: valueKey,
          };
        }
      }
    }
  }

  if (type === 't-test' && testType === 'paired' && gridHeaders.length >= 2) {
    const bCol =
      mapping.beforeColumn && gridHeaders.includes(mapping.beforeColumn)
        ? mapping.beforeColumn
        : gridHeaders[0]!;
    const aCol =
      mapping.afterColumn && gridHeaders.includes(mapping.afterColumn)
        ? mapping.afterColumn
        : gridHeaders[1]!;
    const rows = expandWideTwoGroupRows(gridData, bCol, aCol, DASHBOARD_PAIR_VALUE_KEY);
    return { gridData: rows, groupCol: DASHBOARD_PAIR_GROUP_KEY, valueCol: DASHBOARD_PAIR_VALUE_KEY };
  }

  if (
    type === 'anova' &&
    (anovaType === 'oneWay' || anovaType === 'repeatedMeasures') &&
    (mapping.groupColumn || mapping.factorAColumn) &&
    mapping.valueColumn &&
    gridHeaders.includes(mapping.groupColumn ?? mapping.factorAColumn ?? '') &&
    gridHeaders.includes(mapping.valueColumn)
  ) {
    return {
      gridData,
      groupCol: mapping.groupColumn ?? mapping.factorAColumn ?? 'group',
      valueCol: mapping.valueColumn,
    };
  }

  if (
    type === 'anova' &&
    anovaType === 'twoWay' &&
    mapping.factorAColumn &&
    mapping.valueColumn &&
    gridHeaders.includes(mapping.factorAColumn) &&
    gridHeaders.includes(mapping.valueColumn)
  ) {
    return { gridData, groupCol: mapping.factorAColumn, valueCol: mapping.valueColumn };
  }

  if (
    type === 'bar-chart' &&
    mapping.groupColumn &&
    mapping.valueColumn &&
    gridHeaders.includes(mapping.groupColumn) &&
    gridHeaders.includes(mapping.valueColumn)
  ) {
    return { gridData, groupCol: mapping.groupColumn, valueCol: mapping.valueColumn };
  }

  /** 基本統計: 群列があれば群別で可視化。無いときだけ単一系列に畳む */
  if (type === 'basic-stats') {
    const valueCol =
      mapping.valueColumn && gridHeaders.includes(mapping.valueColumn)
        ? mapping.valueColumn
        : gridHeaders.length === 1
          ? gridHeaders[0]!
          : gridHeaders.find((h) =>
              gridData.some((row) => keisukeNumericCellValue(row[h]) !== null)
            );
    if (valueCol) {
      const groupColCandidate =
        mapping.groupColumn && gridHeaders.includes(mapping.groupColumn)
          ? mapping.groupColumn
          : gridHeaders.find(
              (h) =>
                h !== valueCol &&
                /^(group|condition|genotype|treatment|strain|arm|群|条件|処理)/i.test(h)
            );
      if (groupColCandidate) {
        const distinct = new Set<string>();
        for (const row of gridData) {
          const g = String(row[groupColCandidate] ?? '').trim();
          if (g) distinct.add(g);
          if (distinct.size >= 2) break;
        }
        if (distinct.size >= 2) {
          return { gridData, groupCol: groupColCandidate, valueCol };
        }
      }

      const axisLabel = mapping.valueAxisLabel?.trim();
      const groupLabel = axisLabel || valueCol;
      const rows: DataRow[] = [];
      for (const row of gridData) {
        const v = keisukeNumericCellValue(row[valueCol]);
        if (v === null) continue;
        rows.push({
          [DASHBOARD_PAIR_GROUP_KEY]: groupLabel,
          [DASHBOARD_PAIR_VALUE_KEY]: v,
        } as DataRow);
      }
      if (rows.length > 0) {
        return {
          gridData: rows,
          groupCol: DASHBOARD_PAIR_GROUP_KEY,
          valueCol: DASHBOARD_PAIR_VALUE_KEY,
        };
      }
    }
  }

  const { groupCol, valueCol } = getGroupAndValueColumns(gridHeaders);
  return { gridData, groupCol, valueCol };
}

/** グループ列・値列でデータをグループ化（TTestView / BarChartView と同じ空セル・数値解釈） */
function groupDataByColumn(
  data: DataRow[],
  groupCol: string,
  valueCol: string
): Map<string, number[]> {
  const map = new Map<string, number[]>();
  for (const row of data) {
    const g = keisukeCategoryLabelFromCell(row[groupCol]);
    if (!g) continue;
    const v = keisukeNumericCellValue(row[valueCol]);
    if (v === null) continue;
    if (!map.has(g)) map.set(g, []);
    map.get(g)!.push(v);
  }
  return map;
}

/** レガシー計助風グラフ: 棒を黒一色にする（論文用） */
const LEGACY_BAR_COLOR = '#000000';

export type DashboardBarErrorBarType = 'none' | 'sd' | 'se' | 'ci';

function errorBarForType(
  center: number,
  stats: ReturnType<typeof calculateBasicStatistics>,
  errorBarType: DashboardBarErrorBarType
): { lower: number; upper: number } | undefined {
  if (errorBarType === 'none') return undefined;
  if (errorBarType === 'sd') {
    return { lower: center - stats.stdDev, upper: center + stats.stdDev };
  }
  if (errorBarType === 'se') {
    return { lower: center - stats.stdError, upper: center + stats.stdError };
  }
  // 95% CI: 平均の CI の半幅を、表示中心（平均または中央値）を軸に対称に表示
  const halfWidth =
    (stats.confidenceInterval.upper - stats.confidenceInterval.lower) / 2;
  return { lower: center - halfWidth, upper: center + halfWidth };
}

/** 箱ひげ図1系列（Tukey のヒゲ: フェンス内の最小・最大） */
export interface DashboardBoxPlotSeries {
  name: string;
  whiskerLow: number;
  q1: number;
  median: number;
  q3: number;
  whiskerHigh: number;
  /** 算術平均（箱ひげ上の平均マーカー用） */
  mean: number;
  outliers: number[];
  n: number;
  color?: string;
}

/**
 * 群別の数値から箱ひげ図用系列を生成（categoryOrder は棒グラフと同じ並びに揃える）
 */
export function computeGroupBoxPlotData(
  data: DataRow[],
  groupCol: string,
  valueCol: string,
  categoryOrder: string[],
  options: { legacyBarStyle?: boolean } = {}
): DashboardBoxPlotSeries[] {
  const { legacyBarStyle = true } = options;
  const map = groupDataByColumn(data, groupCol, valueCol);
  return categoryOrder
    .map((name, index) => {
      const valuesRaw = map.get(name);
      if (!valuesRaw?.length) return null;
      const sorted = [...valuesRaw].sort((a, b) => a - b);
      if (sorted.length === 1) {
        const v = sorted[0]!;
        return {
          name,
          whiskerLow: v,
          q1: v,
          median: v,
          q3: v,
          whiskerHigh: v,
          mean: v,
          outliers: [] as number[],
          n: 1,
          color: legacyBarStyle ? LEGACY_BAR_COLOR : getColor(index),
        };
      }
      const stats = calculateBasicStatistics(sorted);
      const lowerFence = stats.q1 - 1.5 * stats.iqr;
      const upperFence = stats.q3 + 1.5 * stats.iqr;
      const inside = sorted.filter((v) => v >= lowerFence && v <= upperFence);
      const whiskerLow = inside.length ? Math.min(...inside) : sorted[0]!;
      const whiskerHigh = inside.length ? Math.max(...inside) : sorted[sorted.length - 1]!;
      const outliers = sorted.filter((v) => v < lowerFence || v > upperFence);
      return {
        name,
        whiskerLow,
        q1: stats.q1,
        median: stats.median,
        q3: stats.q3,
        whiskerHigh,
        mean: stats.mean,
        outliers,
        n: stats.n,
        color: legacyBarStyle ? LEGACY_BAR_COLOR : getColor(index),
      };
    })
    .filter((x) => x != null) as DashboardBoxPlotSeries[];
}

/** 散布図（ジッタープロット）の1点 */
export interface DashboardScatterPoint {
  group: string;
  value: number;
  color?: string;
}

/**
 * 群別の全観測値を散布図用の点リストに変換する。
 * 描画時のジッターは deterministic（値 + インデックスで seed）にするため座標は含まない。
 */
export function computeGroupScatterData(
  data: DataRow[],
  groupCol: string,
  valueCol: string,
  categoryOrder: string[],
  options: { legacyStyle?: boolean } = {}
): DashboardScatterPoint[] {
  const { legacyStyle = true } = options;
  const map = groupDataByColumn(data, groupCol, valueCol);
  const result: DashboardScatterPoint[] = [];
  for (const name of categoryOrder) {
    const values = map.get(name);
    if (!values?.length) continue;
    for (const v of values) {
      result.push({ group: name, value: v, color: legacyStyle ? LEGACY_BAR_COLOR : undefined });
    }
  }
  return result;
}

/** 散布図の Y 軸レンジ（全観測値を含む） */
export function getYDomainForScatter(points: DashboardScatterPoint[]): [number, number] {
  if (points.length === 0) return [0, 1];
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  if (min === max) return calculateNiceDomain([min - 1, max + 1], 5);
  const normalizedMin = min < 0 ? Math.min(0, min) : min;
  return calculateNiceDomain([normalizedMin, max], 5);
}

/** 箱ひげ＋外れ値を含む Y 軸レンジ */
export function getYDomainForBoxPlot(boxes: DashboardBoxPlotSeries[]): [number, number] {
  if (boxes.length === 0) {
    return [0, 1];
  }
  let min = Infinity;
  let max = -Infinity;
  for (const b of boxes) {
    min = Math.min(min, b.whiskerLow, b.q1, b.median, b.q3, b.whiskerHigh);
    max = Math.max(max, b.whiskerLow, b.q1, b.median, b.q3, b.whiskerHigh);
    for (const o of b.outliers) {
      min = Math.min(min, o);
      max = Math.max(max, o);
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [0, 1];
  }
  if (min === max) {
    return calculateNiceDomain([min - 1, max + 1], 5);
  }
  const normalizedMin = min < 0 ? Math.min(0, min) : min;
  return calculateNiceDomain([normalizedMin, max], 5);
}

/**
 * レガシー棒グラフ用: グループ別平均または中央値・エラーバー（なし / SD / SE / 95%CI）を BarData[] で返す
 * legacyBarStyle: true のときは棒を黒一色にしてレガシー計助のデザインに合わせる
 */
export function computeGroupBarData(
  data: DataRow[],
  groupCol: string,
  valueCol: string,
  options: {
    errorBarType?: DashboardBarErrorBarType;
    barCenter?: 'mean' | 'median';
    legacyBarStyle?: boolean;
  } = {}
): BarData[] {
  const {
    errorBarType = 'sd',
    barCenter = 'mean',
    legacyBarStyle = true,
  } = options;
  const map = groupDataByColumn(data, groupCol, valueCol);
  const entries = Array.from(map.entries()).filter(([, values]) => values.length > 0);
  return entries.map(([name, values], index) => {
    const stats = calculateBasicStatistics(values);
    const center = barCenter === 'median' ? stats.median : stats.mean;
    return {
      name,
      value: center,
      errorBar: errorBarForType(center, stats, errorBarType),
      n: stats.n,
      color: legacyBarStyle ? LEGACY_BAR_COLOR : getColor(index),
    };
  });
}

export interface SignificanceMark {
  /** 左バー index */
  leftIndex: number;
  /** 右バー index */
  rightIndex: number;
  /** 表示する記号 (*, **, ***) */
  symbol: string;
  /** P値（表示形式が p のときに使用） */
  pValue: number;
  /** 描画時の Y オフセット段階（0, 1, 2... で重ねない） */
  level: number;
}

/**
 * P 値から有意差記号を返す
 */
export function pValueToSymbol(p: number): string {
  if (p < 0.001) return '***';
  if (p < 0.01) return '**';
  if (p < 0.05) return '*';
  return '';
}

/**
 * 有意差ペアのリストから、重ならないように level を付与したマーク配列を生成
 * （同じ level のマークは横に並ぶので、Y を level に応じて上げて重ねる）
 * displayMode: 'all' は全件、'topN' は pValue の小さい順に topN 件のみ
 */
export function buildSignificanceMarks(
  pairs: Array<{ leftIndex: number; rightIndex: number; pValue: number }>,
  options?: { displayMode?: 'all' | 'topN'; topN?: number }
): SignificanceMark[] {
  let filtered = pairs.filter((p) => pValueToSymbol(p.pValue));
  if (options?.displayMode === 'topN' && typeof options?.topN === 'number' && options.topN >= 1) {
    filtered = [...filtered]
      .sort((a, b) => a.pValue - b.pValue)
      .slice(0, options.topN);
  }
  const withSymbol = filtered.map((p) => ({ ...p, symbol: pValueToSymbol(p.pValue) }));
  const levels = assignLevelsNoOverlap(withSymbol);
  return withSymbol.map((p, i) => ({
    leftIndex: p.leftIndex,
    rightIndex: p.rightIndex,
    symbol: p.symbol,
    pValue: p.pValue,
    level: levels[i],
  }));
}

/** 区間 [leftIndex, rightIndex] が重ならないように level を割り当て */
function assignLevelsNoOverlap(
  pairs: Array<{ leftIndex: number; rightIndex: number }>
): number[] {
  const levels = new Array(pairs.length).fill(0);
  for (let i = 0; i < pairs.length; i++) {
    const a = Math.min(pairs[i].leftIndex, pairs[i].rightIndex);
    const b = Math.max(pairs[i].leftIndex, pairs[i].rightIndex);
    const used = new Set<number>();
    for (let j = 0; j < i; j++) {
      const c = Math.min(pairs[j].leftIndex, pairs[j].rightIndex);
      const d = Math.max(pairs[j].leftIndex, pairs[j].rightIndex);
      if (intervalsOverlap(a, b, c, d)) used.add(levels[j]);
    }
    let l = 0;
    while (used.has(l)) l++;
    levels[i] = l;
  }
  return levels;
}

function intervalsOverlap(a: number, b: number, c: number, d: number): boolean {
  return !(b < c || d < a);
}

export {
  clipboardLooksLikeSpreadsheetGrid,
  clipboardPlainTextFromEvent,
  parseClipboardTsvMatrix,
} from './keisukeDashboardClipboardUtils';
